import type { Transaction } from 'dexie'

import type { LocalEntity, OutboxMutation } from '../../domain/sync'

const LEGACY_CONTENT_TYPES = new Set([
  'event', 'poll', 'pin', 'challenge_completion', 'timeline_entry',
])
const LEGACY_NOTIFICATION_IDS = new Set(['notification-demo-agenda', 'notification-demo-photo'])
const LEGACY_CHALLENGE_IDS = new Set(
  Array.from({ length: 7 }, (_, index) => `77000000-0000-4000-8000-${String(index + 1).padStart(12, '0')}`),
)

type ChallengeRow = { id: string }
type ChallengeAssignmentRow = { key: string; challengeId: string }
type NotificationRow = { id: string }
type MemoryRouteRow = { tripId: string; generatedAt: string }

export type TripTruthMigrationReport = {
  entitiesRemoved: number
  challengesRemoved: number
  assignmentsRemoved: number
  notificationsRemoved: number
  routesRemoved: number
}

export function isRecognizedSyncedLegacyEntity(
  entity: LocalEntity,
  protectedEntityIds: ReadonlySet<string>,
): boolean {
  return entity.syncState === 'synced'
    && LEGACY_CONTENT_TYPES.has(entity.entityType)
    && entity.lastMutationId.startsWith('seed:')
    && !protectedEntityIds.has(entity.id)
}

export async function migrateTripTruthData(transaction: Transaction): Promise<TripTruthMigrationReport> {
  const entityTable = transaction.table<LocalEntity>('entities')
  const outboxTable = transaction.table<OutboxMutation>('outbox')
  const challengeTable = transaction.table<ChallengeRow>('challenges')
  const assignmentTable = transaction.table<ChallengeAssignmentRow>('challengeAssignments')
  const notificationTable = transaction.table<NotificationRow>('notifications')
  const routeTable = transaction.table<MemoryRouteRow>('memoryRoutes')

  const actionable = await outboxTable.filter((mutation) => mutation.state !== 'synced').toArray()
  const protectedEntityIds = new Set(actionable.map((mutation) => mutation.entityId))
  const legacyEntities = await entityTable.filter((entity) => isRecognizedSyncedLegacyEntity(entity, protectedEntityIds)).primaryKeys()
  const legacyChallenges = await challengeTable.filter((challenge) => LEGACY_CHALLENGE_IDS.has(challenge.id)).primaryKeys()
  const legacyAssignments = await assignmentTable.filter((assignment) => LEGACY_CHALLENGE_IDS.has(assignment.challengeId)).primaryKeys()
  const legacyNotifications = await notificationTable.filter((notification) => LEGACY_NOTIFICATION_IDS.has(notification.id)).primaryKeys()
  const legacyRoutes = await routeTable.filter((route) => route.generatedAt === '2026-08-29T18:00:00.000Z').primaryKeys()

  await entityTable.bulkDelete(legacyEntities)
  await challengeTable.bulkDelete(legacyChallenges)
  await assignmentTable.bulkDelete(legacyAssignments)
  await notificationTable.bulkDelete(legacyNotifications)
  await routeTable.bulkDelete(legacyRoutes)

  return {
    entitiesRemoved: legacyEntities.length,
    challengesRemoved: legacyChallenges.length,
    assignmentsRemoved: legacyAssignments.length,
    notificationsRemoved: legacyNotifications.length,
    routesRemoved: legacyRoutes.length,
  }
}
